import { useState } from 'react';
import { Mail, UserPlus } from 'lucide-react';
import { companiesApi, type TeamMember } from '../services/api';
import type { OrganizationSnapshot } from './model';
import { initials } from './ReportingCanvas';
import { Field } from './EntityInspector';

export default function PeoplePanel({
  companyId,
  members,
  snapshot,
  canInvite,
  onRefresh,
  error,
}: {
  companyId: string;
  members: TeamMember[];
  snapshot: OrganizationSnapshot;
  canInvite: boolean;
  onRefresh: () => void;
  error?: string;
}) {
  const [search, setSearch] = useState('');
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [failure, setFailure] = useState('');
  const invite = async () => {
    setBusy(true);
    setFailure('');
    setMessage('');
    try {
      const response = await companiesApi.inviteUser(companyId, { email: email.trim(), role });
      if (!response.success || !response.data)
        throw new Error(response.error || 'The invitation could not be sent.');
      setMessage(
        response.data.status === 'added'
          ? `${response.data.email} was added to the company.`
          : `Invitation sent to ${response.data.email}.`
      );
      setEmail('');
      onRefresh();
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not invite this person.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="org-people-body">
      <div className="org-outline-intro">
        <span className="org-eyebrow">CURRENT COMPANY PEOPLE</span>
        <p>
          Assign people to roles and teams from the chart. One person can hold more than one role.
        </p>
      </div>
      <label className="org-field">
        <span className="org-sr-only">Search company people</span>
        <input
          type="search"
          placeholder="Search people…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      {(error || failure) && (
        <p className="org-error" role="alert">
          {failure || error}
        </p>
      )}
      {message && (
        <p className="org-success" role="status">
          <Mail size={14} />
          {message}
        </p>
      )}
      <div className="org-directory">
        {members
          .filter((person) =>
            `${person.name} ${person.email}`
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase())
          )
          .map((person) => {
            const roles = snapshot.positions.filter((p) =>
              p.assignments.some((a) => a.userId === person.id)
            );
            return (
              <div key={person.membershipId || person.id} className="org-person-row">
                <span className="org-avatar">
                  {person.photo ? <img src={person.photo} alt="" /> : initials(person.name)}
                </span>
                <div>
                  <strong>{person.name || person.email}</strong>
                  <small>{person.email}</small>
                  <span className="org-directory-role">
                    {person.status === 'invited'
                      ? 'Invitation pending'
                      : roles.length
                        ? roles.map((p) => p.title).join(' · ')
                        : 'No role assigned'}
                  </span>
                </div>
              </div>
            );
          })}
        {!members.length && !error && (
          <p className="org-muted">No company members are available.</p>
        )}
      </div>
      {canInvite && (
        <section className="org-invite-section">
          {!inviting ? (
            <button
              type="button"
              className="org-button org-button-primary"
              onClick={() => setInviting(true)}
            >
              <UserPlus size={14} />
              Invite member
            </button>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void invite();
              }}
            >
              <h3>Invite a company member</h3>
              <Field label="Email address">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@company.com"
                  disabled={busy}
                />
              </Field>
              <Field label="Company access">
                <select
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  disabled={busy}
                >
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                </select>
              </Field>
              <div className="org-row">
                <button
                  type="submit"
                  className="org-button org-button-primary"
                  disabled={busy || !email.trim()}
                >
                  {busy ? 'Sending…' : 'Send invitation'}
                </button>
                <button
                  type="button"
                  className="org-text-button"
                  disabled={busy}
                  onClick={() => setInviting(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </section>
      )}
    </div>
  );
}
